import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {alpha, Box, CircularProgress, IconButton, Theme, Typography, useTheme} from '@mui/material';
import Markdown from 'react-markdown';
import {useTranslation} from 'react-i18next';
import remarkGfm from 'remark-gfm';
import AiChartBlock, {parseAiChart} from './AiChartBlock';
import MermaidBlock from './MermaidBlock';

type AiMarkdownViewerProps = {
  content: string;
  loading: boolean;
  fill?: boolean;
  variant?: 'chat' | 'result';
  mermaidPresentation?: 'agent';
};

type ResultCodeBlockProps = {
  code: string;
  language?: string;
};

type CodeNode = {
  properties: {className?: string | string[]};
  children: Array<{value?: string}>;
};

export const getResultCodeHeaderStyles = (theme: Theme) => ({
  bgcolor: alpha(theme.palette.primary.main, 0.1)
});

export const getResultMarkdownStyles = (theme: Theme) => ({
  '& h1': {
    mt: 2.5,
    mb: 1,
    px: 1,
    py: 0.75,
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.h4.fontWeight,
    color: theme.palette.text.primary,
    bgcolor: alpha(theme.palette.primary.main, 0.1),
    borderLeft: 4,
    borderColor: theme.palette.primary.main,
    borderRadius: 1
  },
  '& h2': {
    mt: 2.25,
    mb: 0.75,
    pl: 1,
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.h5.fontWeight,
    color: theme.palette.text.primary,
    borderLeft: 3,
    borderColor: alpha(theme.palette.primary.main, 0.55)
  },
  '& h3, & h4': {
    mt: 1.75,
    pl: 0.75,
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    borderLeft: 3,
    borderColor: alpha(theme.palette.secondary.main, 0.55)
  },
  '& blockquote': {
    my: 1,
    mx: 0,
    py: 0.5,
    pl: 1.5,
    borderLeft: 4,
    borderColor: theme.palette.primary.main,
    color: 'text.secondary',
    bgcolor: alpha(theme.palette.primary.main, 0.08)
  },
  '& table': {
    my: 1.5,
    display: 'block',
    overflowX: 'auto',
    border: 1,
    borderColor: 'divider',
    borderRadius: 1
  },
  '& th': {bgcolor: alpha(theme.palette.secondary.main, 0.1), fontWeight: 'bold'},
  '& tbody tr:nth-of-type(even)': {bgcolor: alpha(theme.palette.primary.main, 0.04)},
  '& th, & td': {py: 1}
});

const ResultCodeBlock = ({code, language}: ResultCodeBlockProps) => {
  const theme = useTheme();
  const {t} = useTranslation();

  const copyCode = () => {
    void window.navigator.clipboard?.writeText(code);
  };

  return (
    <Box
      sx={{
        my: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 32,
          px: 1,
          ...getResultCodeHeaderStyles(theme)
        }}
      >
        <Typography
          variant="caption"
          sx={{fontFamily: theme.typography.fontFamily, color: 'text.secondary'}}
        >
          {language}
        </Typography>
        <IconButton aria-label={t('コードをコピー')} size="small" onClick={copyCode}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Box>
      <Box component="pre" sx={{m: 0, p: 1.5, overflow: 'auto'}}>
        <code>{code}</code>
      </Box>
    </Box>
  );
};

const AiMarkdownViewer = ({
  content,
  loading,
  fill = true,
  variant = 'chat',
  mermaidPresentation
}: AiMarkdownViewerProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        flex: fill ? 1 : 'initial',
        px: 3,
        py: 1,
        overflow: fill ? 'auto' : 'visible',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        lineHeight: 1.5,
        '& p': {my: 0.5},
        '& ul, & ol': {my: 0.5, pl: 3},
        '& li': {my: 0.25},
        '& h1, & h2, & h3, & h4': {mt: 1.5, mb: 0.5, lineHeight: 1.3},
        '& pre': {
          my: 0.5,
          p: 1,
          borderRadius: 1,
          bgcolor: 'background.paper',
          overflow: 'auto'
        },
        '& code': {fontSize: theme.typography.caption.fontSize},
        '& hr': {my: 1, borderColor: theme.palette.divider},
        '& table': {borderCollapse: 'collapse', my: 0.5, width: '100%'},
        '& th, & td': {
          border: 1,
          borderColor: 'divider',
          px: 1.5,
          py: 0.5,
          textAlign: 'left'
        },
        '& th': {bgcolor: 'action.selected', fontWeight: 'bold'},
        ...(variant === 'result' && getResultMarkdownStyles(theme))
      }}
    >
      {loading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            pre({children, node}) {
              const codeNode = node?.children.find(
                child => child.type === 'element' && child.tagName === 'code'
              ) as CodeNode | undefined;
              if (codeNode) {
                const className = Array.isArray(codeNode.properties.className)
                  ? codeNode.properties.className.join(' ')
                  : codeNode.properties.className;
                const language =
                  typeof className === 'string'
                    ? className.match(/language-([^\s]+)/)?.[1]
                    : undefined;
                const code = codeNode.children
                  .map(child => ('value' in child ? child.value : ''))
                  .join('');
                if (language === 'mermaid') {
                  return <MermaidBlock chart={code} presentation={mermaidPresentation} />;
                }
                if (language === 'lfc-chart' && variant === 'result' && parseAiChart(code)) {
                  return <AiChartBlock source={code} />;
                }
                if (variant === 'result') {
                  return <ResultCodeBlock code={code} language={language} />;
                }
              }
              return <pre>{children}</pre>;
            }
          }}
        >
          {content}
        </Markdown>
      )}
    </Box>
  );
};

export default AiMarkdownViewer;
